import React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import type { CreditBalance } from '../../../shared/domain/credits/types.no-deps'
import { emptyDict, type Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import { isNil } from '../../../shared/lib/functional/nil.no-deps'

type ListenerHandler = (balance: CreditBalance) => void

const sharedBalancesMap: Dictionary<CreditBalance> = emptyDict()
const listenersMap: Dictionary<Set<ListenerHandler>> = emptyDict()
const intervalsMap: Dictionary<number> = emptyDict()

/**
 * React hook component to fetch a credit balance every 2 minutes and keep all component instances
 * aware of the balance changes.
 *
 * The backend supports having multiple separate credit balances, hence the use of dict to track intervals,
 * balances and listeners.
 */
export function useCreditBalance(
    deps: CommonDeps,
    creditBalanceId: string,
    isActive: boolean = true
): [CreditBalance, (balance: CreditBalance) => void] {
    const [creditBalance, setCreditBalance] = React.useState<Nullable<CreditBalance>>(null)
    const dictKey = getDictKey(deps, creditBalanceId)

    React.useEffect(() => {
        if (!isActive) {
            return
        }

        setCreditBalance(getSharedCreditBalance(dictKey))
        addListener(dictKey, setCreditBalance)

        if (listenersMap[dictKey].size === 1) {
            fetchCreditBalance(deps, creditBalanceId)

            const interval = setInterval(() => fetchCreditBalance(deps, creditBalanceId), 2 * 60 * 1000)
            addInterval(dictKey, interval)
        }

        return () => {
            deleteListener(dictKey, setCreditBalance)

            // Removes listeners and clears the interval and shared balance when unmounting the last component.
            const listeners = getListeners(dictKey)
            if (listeners.size === 0) {
                deleteInterval(dictKey)
                deleteListeners(dictKey)

                setSharedCreditBalance(dictKey, null)
            }
        }
    }, [isActive])

    return [
        creditBalance,

        (balance: CreditBalance) => {
            // Allow updating the balance from outside the hook, eg. right after generating an step.
            setSharedCreditBalance(dictKey, balance)
        },
    ]
}

async function fetchCreditBalance(deps: CommonDeps, creditBalanceId: string) {
    if (!(await deps.isOnline())) {
        return
    }

    const dictKey = getDictKey(deps, creditBalanceId)
    const response = await deps.httpClient.getCreditBalance(creditBalanceId)

    setSharedCreditBalance(dictKey, response.balance)
}

function getDictKey(deps: CommonDeps, creditBalanceId: string) {
    // The dictionary key includes the customer slug as precaution in case the cached balance isn't deleted.
    return `${deps.customerSlug}_${creditBalanceId}`
}

/* Shared credit balances */
function getSharedCreditBalance(dictKey: string): Nullable<CreditBalance> {
    return sharedBalancesMap[dictKey] || null
}

function setSharedCreditBalance(dictKey: string, creditBalance: Nullable<CreditBalance>) {
    if (isNil(creditBalance)) {
        delete sharedBalancesMap[dictKey]
    } else {
        sharedBalancesMap[dictKey] = creditBalance
    }

    const listeners = getListeners(dictKey)
    listeners.forEach((listenerHandler) => listenerHandler(creditBalance))
}

/* Listeners */
function getListeners(dictKey: string): Set<ListenerHandler> {
    return listenersMap[dictKey] || new Set()
}

function addListener(dictKey: string, listener: ListenerHandler) {
    listenersMap[dictKey] = getListeners(dictKey).add(listener)
}

function deleteListener(dictKey: string, listener: ListenerHandler) {
    listenersMap[dictKey].delete(listener)
}

function deleteListeners(dictKey: string) {
    delete listenersMap[dictKey]
}

/* Intervals tracking */
function getInterval(dictKey: string) {
    return intervalsMap[dictKey]
}

function deleteInterval(dictKey: string) {
    const interval = getInterval(dictKey)
    if (interval) {
        clearInterval(interval)
    }

    delete intervalsMap[dictKey]
}

function addInterval(dictKey: string, interval: number) {
    deleteInterval(dictKey)

    intervalsMap[dictKey] = interval
}
